import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Shield, Plus, Edit, Trash2, RefreshCw, AlertTriangle,
  Users, Eye, Crown, UserCheck, Settings, Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default system roles that cannot be deleted
const SYSTEM_ROLES = ['super_admin', 'account_owner', 'admin'];

// Permission categories
const PERMISSION_CATEGORIES = {
  contacts: {
    label: 'Contacts',
    permissions: ['view_contacts', 'create_contacts', 'edit_contacts', 'delete_contacts', 'export_contacts']
  },
  deals: {
    label: 'Deals',
    permissions: ['view_deals', 'create_deals', 'edit_deals', 'delete_deals']
  },
  teams: {
    label: 'Teams',
    permissions: ['view_teams', 'manage_teams', 'assign_members']
  },
  reports: {
    label: 'Reports',
    permissions: ['view_reports', 'export_reports', 'view_analytics']
  },
  settings: {
    label: 'Settings',
    permissions: ['view_settings', 'manage_settings', 'manage_integrations']
  },
  users: {
    label: 'User Management',
    permissions: ['view_users', 'create_users', 'edit_users', 'delete_users']
  }
};

const RoleManagement = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    level: 5
  });

  useEffect(() => {
    console.log('RoleManagement: token available:', !!token);
    if (token) {
      fetchRoles();
    } else {
      // Token not yet available, wait a bit
      const timeout = setTimeout(() => {
        if (!token) {
          console.log('RoleManagement: No token after timeout, using defaults');
          setDefaultRoles();
          setLoading(false);
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [token]);

  const fetchRoles = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    console.log('RoleManagement: Fetching roles...');
    setLoading(true);
    try {
      console.log('RoleManagement: Making request to', `${API}/roles?token=${token.substring(0, 20)}...`);
      const response = await axios.get(`${API}/roles?token=${token}`);
      console.log('Roles response:', response.data);
      if (response.data.success) {
        setRoles(response.data.roles || []);
      } else {
        // Fallback to default roles if API returns failure
        setDefaultRoles();
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // If no roles endpoint exists yet, use default roles
      setDefaultRoles();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultRoles = () => {
    setRoles([
      { id: 'super_admin', name: 'super_admin', display_name: 'Super Admin', description: 'Full system access', level: 0, is_system: true, permissions: ['*'] },
      { id: 'account_owner', name: 'account_owner', display_name: 'Account Owner', description: 'Full account access', level: 1, is_system: true, permissions: ['*'] },
      { id: 'admin', name: 'admin', display_name: 'Admin', description: 'Administrative access', level: 2, is_system: true, permissions: ['view_contacts', 'create_contacts', 'edit_contacts', 'delete_contacts', 'view_deals', 'create_deals', 'edit_deals', 'view_teams', 'manage_teams', 'view_reports', 'view_settings', 'manage_settings', 'view_users', 'create_users', 'edit_users'] },
      { id: 'sales_manager', name: 'sales_manager', display_name: 'Sales Manager', description: 'Manage sales team', level: 3, is_system: false, permissions: ['view_contacts', 'create_contacts', 'edit_contacts', 'view_deals', 'create_deals', 'edit_deals', 'view_teams', 'assign_members', 'view_reports'] },
      { id: 'sales_rep', name: 'sales_rep', display_name: 'Sales Agent', description: 'Sales representative', level: 4, is_system: false, permissions: ['view_contacts', 'create_contacts', 'edit_contacts', 'view_deals', 'create_deals', 'edit_deals'] },
      { id: 'viewer', name: 'viewer', display_name: 'Viewer', description: 'Read-only access', level: 5, is_system: false, permissions: ['view_contacts', 'view_deals', 'view_reports'] }
    ]);
  };

  const handleCreateRole = async () => {
    if (!formData.name || !formData.display_name) {
      toast.error('Role name and display name are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/roles?token=${token}`, {
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions,
        level: formData.level
      });
      
      if (response.data.success) {
        toast.success('Role created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchRoles();
      } else {
        toast.error(response.data.error || 'Failed to create role');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/roles/${selectedRole.id}?token=${token}`, {
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions,
        level: formData.level
      });
      
      if (response.data.success) {
        toast.success('Role updated successfully');
        setShowEditDialog(false);
        setSelectedRole(null);
        resetForm();
        fetchRoles();
      } else {
        toast.error(response.data.error || 'Failed to update role');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      const response = await axios.delete(`${API}/roles/${selectedRole.id}?token=${token}`);
      
      if (response.data.success) {
        toast.success('Role deleted successfully');
        setShowDeleteDialog(false);
        setSelectedRole(null);
        fetchRoles();
      } else {
        toast.error(response.data.error || 'Failed to delete role');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || '',
      permissions: role.permissions || [],
      level: role.level || 5
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      level: 5
    });
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const toggleCategoryPermissions = (category) => {
    const categoryPerms = PERMISSION_CATEGORIES[category].permissions;
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const getRoleIcon = (role) => {
    if (role.name === 'super_admin' || role.name === 'account_owner') return Crown;
    if (role.name === 'admin') return Shield;
    if (role.name === 'sales_manager') return Users;
    if (role.name === 'viewer') return Eye;
    return UserCheck;
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'bg-red-500',
      account_owner: 'bg-amber-500',
      admin: 'bg-purple-500',
      sales_manager: 'bg-blue-500',
      sales_rep: 'bg-green-500',
      viewer: 'bg-slate-500'
    };
    return colors[role.name] || 'bg-teal-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin mr-2" size={24} />
        <span>Loading roles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="role-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="text-purple-600" />
            Role Management
          </h1>
          <p className="text-slate-500 mt-1">Create and manage user roles and permissions</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="create-role-btn"
        >
          <Plus size={16} className="mr-2" />
          Create Role
        </Button>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Roles ({roles.length})</CardTitle>
          <CardDescription>Manage access levels and permissions for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Level</TableHead>
                <TableHead className="text-center">Permissions</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map(role => {
                const RoleIcon = getRoleIcon(role);
                const isSystem = SYSTEM_ROLES.includes(role.name);
                return (
                  <TableRow key={role.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getRoleColor(role)}`}>
                          <RoleIcon size={16} className="text-white" />
                        </div>
                        <div>
                          <span className="font-medium">{role.display_name}</span>
                          <span className="text-xs text-slate-400 block">{role.name}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{role.description || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{role.level}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {role.permissions?.includes('*') ? 'Full Access' : `${role.permissions?.length || 0} permissions`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {isSystem ? (
                        <Badge className="bg-slate-500">
                          <Lock size={12} className="mr-1" />
                          System
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-teal-600 border-teal-200">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(role)}
                          data-testid={`edit-role-${role.id}`}
                        >
                          <Edit size={14} />
                        </Button>
                        {!isSystem && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(role)}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="text-purple-600" size={20} />
              Create New Role
            </DialogTitle>
            <DialogDescription>
              Define a new role with specific permissions for your organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role ID *</Label>
                <Input
                  id="role-name"
                  placeholder="e.g., team_lead"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
                <p className="text-xs text-slate-500">Lowercase, no spaces (used internally)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display-name">Display Name *</Label>
                <Input
                  id="display-name"
                  placeholder="e.g., Team Lead"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Describe the role's responsibilities"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hierarchy Level (0 = highest)</Label>
              <Input
                type="number"
                min="3"
                max="10"
                value={formData.level}
                onChange={(e) => setFormData(prev => ({ ...prev, level: parseInt(e.target.value) || 5 }))}
              />
            </div>
            
            {/* Permissions */}
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="border rounded-lg p-4 space-y-4">
                {Object.entries(PERMISSION_CATEGORIES).map(([key, category]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={category.permissions.every(p => formData.permissions.includes(p))}
                        onCheckedChange={() => toggleCategoryPermissions(key)}
                      />
                      <span className="font-medium">{category.label}</span>
                    </div>
                    <div className="ml-6 flex flex-wrap gap-2">
                      {category.permissions.map(permission => (
                        <label key={permission} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <Checkbox
                            checked={formData.permissions.includes(permission)}
                            onCheckedChange={() => togglePermission(permission)}
                          />
                          <span className="text-slate-600">{permission.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRole} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Plus size={14} className="mr-2" />}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="text-blue-600" size={20} />
              Edit Role: {selectedRole?.display_name}
            </DialogTitle>
            <DialogDescription>
              Modify role settings and permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role ID</Label>
                <Input value={formData.name} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-display-name">Display Name *</Label>
                <Input
                  id="edit-display-name"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hierarchy Level</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={formData.level}
                onChange={(e) => setFormData(prev => ({ ...prev, level: parseInt(e.target.value) || 5 }))}
                disabled={SYSTEM_ROLES.includes(selectedRole?.name)}
              />
            </div>
            
            {/* Permissions */}
            {!selectedRole?.permissions?.includes('*') && (
              <div className="space-y-3">
                <Label>Permissions</Label>
                <div className="border rounded-lg p-4 space-y-4">
                  {Object.entries(PERMISSION_CATEGORIES).map(([key, category]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={category.permissions.every(p => formData.permissions.includes(p))}
                          onCheckedChange={() => toggleCategoryPermissions(key)}
                        />
                        <span className="font-medium">{category.label}</span>
                      </div>
                      <div className="ml-6 flex flex-wrap gap-2">
                        {category.permissions.map(permission => (
                          <label key={permission} className="flex items-center gap-1.5 text-sm cursor-pointer">
                            <Checkbox
                              checked={formData.permissions.includes(permission)}
                              onCheckedChange={() => togglePermission(permission)}
                            />
                            <span className="text-slate-600">{permission.replace(/_/g, ' ')}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectedRole?.permissions?.includes('*') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Crown className="text-amber-600 mt-0.5" size={18} />
                <div>
                  <p className="font-medium text-amber-800">Full Access Role</p>
                  <p className="text-sm text-amber-600">This role has unrestricted access to all features. Permissions cannot be modified.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Delete Role
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role "{selectedRole?.display_name}"? 
              Users with this role will need to be reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteRole} 
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Trash2 size={14} className="mr-2" />}
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;
